import React from 'react';
import { FONT_BODY } from './theme';
import { ModalShell, ModalHeader, FieldLabel, FieldError, TextField, CtaButton } from './ui';
import { PlayIcon, BigCheckIcon } from './icons';

export type AccessKind = 'video' | 'access';

const AccessModal: React.FC<{
  kind: AccessKind;
  title: string;
  pin: string;
  wa: string;
  err: boolean;
  sent: boolean;
  onClose: () => void;
  onPin: (v: string) => void;
  onWa: (v: string) => void;
  onSubmit: () => void;
}> = ({ kind, title, pin, wa, err, sent, onClose, onPin, onWa, onSubmit }) => {
  const isVideo = kind === 'video';
  const isForm = kind === 'access' && !sent;
  const isDone = kind === 'access' && sent;
  return (
    <ModalShell zIndex={100} panelMaxWidth={470} onClose={onClose}>
      <ModalHeader title={title} onClose={onClose} />
      <div style={{ padding: 'clamp(18px,3vw,26px)' }}>
        {isVideo && (
          <>
            <div
              style={{
                aspectRatio: '16/9',
                borderRadius: 14,
                background: 'linear-gradient(135deg,#0F2D6B,#2563EB)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
              }}
            >
              <span
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayIcon size={20} />
              </span>
              <span style={{ font: `600 13px ${FONT_BODY}`, color: 'rgba(255,255,255,.82)' }}>
                Product film — placeholder
              </span>
            </div>
            <p style={{ font: `400 13px/1.55 ${FONT_BODY}`, color: '#64748B', margin: '14px 0 0' }}>
              Drop the final 60-second film here. Until then this stands in so the flow can be reviewed end to end.
            </p>
          </>
        )}

        {isForm && (
          <>
            <h3
              style={{
                font: `800 clamp(19px,2.4vw,23px)/1.2 ${FONT_BODY}`,
                letterSpacing: '-.028em',
                color: '#0F172A',
                margin: 0,
              }}
            >
              Check your 5-km availability
            </h3>
            <p style={{ font: `400 13.5px/1.55 ${FONT_BODY}`, color: '#475569', margin: '9px 0 18px' }}>
              We work with one school in every 5-km radius. Enter your PIN code and WhatsApp number — your free roadmap
              is delivered there.
            </p>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <FieldLabel>School PIN code</FieldLabel>
                <TextField
                  value={pin}
                  onChange={(e) => onPin(e.target.value)}
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="e.g. 560034"
                  fontSize={15}
                  padding="13px 15px"
                />
              </div>
              <div>
                <FieldLabel>WhatsApp number</FieldLabel>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span
                    style={{
                      flex: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 13px',
                      background: '#EFF6FF',
                      border: '1px solid #CFDFF6',
                      borderRadius: 11,
                      font: `600 14px ${FONT_BODY}`,
                      color: '#475569',
                    }}
                  >
                    +91
                  </span>
                  <TextField
                    value={wa}
                    onChange={(e) => onWa(e.target.value)}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit mobile"
                    fontSize={15}
                    padding="13px 15px"
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </div>
              </div>
            </div>
            {err && <FieldError fontSize={12.5} margin="10px 0 0">Enter a valid 6-digit PIN code and 10-digit mobile number.</FieldError>}
            <CtaButton
              variant="block"
              onClick={onSubmit}
              style={{
                width: '100%',
                margin: '18px 0 0',
                borderRadius: 11,
                padding: 15,
                font: `700 15px ${FONT_BODY}`,
                boxShadow: '0 10px 24px rgba(37,99,235,.26)',
              }}
            >
              Request early access
            </CtaButton>
            <p style={{ font: `400 12px/1.5 ${FONT_BODY}`, color: '#64748B', margin: '12px 0 0' }}>
              Prototype: nothing is sent and no details are stored.
            </p>
          </>
        )}

        {isDone && (
          <div style={{ textAlign: 'center', padding: '6px 0' }}>
            <div
              style={{
                width: 56,
                height: 56,
                margin: '0 auto 16px',
                borderRadius: '50%',
                background: '#ECFDF3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BigCheckIcon />
            </div>
            <h3
              style={{
                font: `800 clamp(19px,2.4vw,23px)/1.2 ${FONT_BODY}`,
                letterSpacing: '-.028em',
                color: '#0F172A',
                margin: 0,
              }}
            >
              Your radius is held for 48 hours.
            </h3>
            <p style={{ font: `400 13.5px/1.55 ${FONT_BODY}`, color: '#475569', margin: '11px 0 20px' }}>
              We&apos;ll send your market snapshot and 3-year roadmap to your WhatsApp, then walk through the first
              three priorities with you.
            </p>
            <CtaButton
              variant="block"
              onClick={onClose}
              style={{ width: '100%', borderRadius: 11, padding: 15, font: `700 15px ${FONT_BODY}` }}
            >
              Done
            </CtaButton>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default AccessModal;
